// ============================================================================
// FoCollectionReportScreen — Reports → Front Office Collection (PMS slice 7d)
// ============================================================================
// The Front Office's receipts and refunds over a period (collection point
// FRONT_OFFICE), by kind (advance at check-in, interim, final settlement,
// refund), by payment method, and day by day with each day's close: difference,
// handed over, deposited, retained, who closed and who verified it.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseScreenShell } from 'components/enterprise-shell';
import ReportHeader, { ReportBody, type ReportHeaderItem } from 'components/reports/ReportHeader';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption } from 'components/ui';
import { foClosingService } from 'api/services/foClosingService';
import { propertyService } from 'api/services/propertyService';
import notificationService from 'services/notificationService';
import { blobErrorMessage, reserveTab, saveReportFile, showPdf, type ReportFormat } from 'utils/reportFile';
import { formatCurrency } from 'utils/currencyFormat';
import { useDefaultProperty } from 'hooks/api/useDefaultProperty';
import { usePropertyLabel } from 'hooks/api/usePropertyLabel';
import {
  FO_RECEIPT_KIND_LABEL, FO_STATUS_LABEL,
  type FoCollectionReport, type FoDaySummary, type FoReceiptRow,
} from 'api/types/foClosing.types';
import 'components/modules/dashboard-module/dashboard.css';

const isoOf = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const todayIso = () => isoOf(new Date());
const monthStartIso = () => { const d = new Date(); d.setDate(1); return isoOf(d); };
const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const money = (v?: number | null) => (v == null ? '—' : formatCurrency(v));

interface Props {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /** RBAC — {'view','export'}. Fail-closed: [] renders no export/print. */
  allowedActions?: string[];
}

const FoCollectionReportScreen: React.FC<Props> = ({ tabId, allowedActions = [] }) => {
  const [propertyId, setPropertyId] = useState('');
  const { defaultPropertyId } = useDefaultProperty();
  useEffect(() => { if (!propertyId && defaultPropertyId) setPropertyId(defaultPropertyId); }, [defaultPropertyId, propertyId]);
  const [from, setFrom] = useState(monthStartIso());
  const [to, setTo] = useState(todayIso());
  const [view, setView] = useState<'days' | 'receipts'>('days');
  const [filterOpen, setFilterOpen] = useState(false);
  const propertyLabelsRef = useRef<Record<string, string>>({});
  const fetchedPropertyLabel = usePropertyLabel(propertyId);
  const propertyLabel = fetchedPropertyLabel || propertyLabelsRef.current[propertyId] || '';

  const enabled = !!propertyId && !!from && !!to && from <= to;
  const [report, setReport] = useState<FoCollectionReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    if (!enabled) { setReport(null); return; }
    setLoading(true);
    setError(null);
    try {
      setReport(await foClosingService.getReport(propertyId, from, to));
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'The report did not load.');
    } finally {
      setLoading(false);
    }
  }, [enabled, propertyId, from, to]);
  useEffect(() => { void load(); }, [load]);

  const loadProperties = useCallback(async (q: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: q || undefined, pageSize: q ? 50 : 500, includeInactive: false });
    const opts = (res.data ?? []).map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
    opts.forEach((o: SelectOption) => { propertyLabelsRef.current[o.value as string] = o.label; });
    return opts;
  }, []);

  const [exporting, setExporting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const exportFile = useCallback(async (format: ReportFormat) => {
    if (!enabled) { notificationService.toast.warning('Select a property and a valid period first.'); return; }
    const tab = format === 'pdf' ? reserveTab() : null;
    (format === 'pdf' ? setPrinting : setExporting)(true);
    try {
      const { blob, disposition } = await foClosingService.exportReport(propertyId, from, to, format);
      if (format === 'pdf') showPdf(blob, tab);
      else saveReportFile(blob, disposition, `FrontOfficeCollection_${from}_${to}.xlsx`);
    } catch (err) {
      tab?.close();
      notificationService.toast.error(await blobErrorMessage(err, format === 'pdf' ? 'Print failed.' : 'Export failed.'));
    } finally {
      (format === 'pdf' ? setPrinting : setExporting)(false);
    }
  }, [enabled, propertyId, from, to]);

  const dayColumns = useMemo<SyutGridColumn<FoDaySummary & { id: string }>[]>(() => [
    { id: 'businessDate', header: 'Date', accessorKey: 'businessDate', size: 110, cell: ({ row }) => formatDate(row.original.businessDate) },
    { id: 'receipts', header: 'Received', accessorKey: 'receipts', size: 110, cell: ({ row }) => formatCurrency(row.original.receipts) },
    { id: 'refunds', header: 'Refunded', accessorKey: 'refunds', size: 100, cell: ({ row }) => row.original.refunds ? formatCurrency(row.original.refunds) : '—' },
    { id: 'cashReceived', header: 'Cash received', accessorKey: 'cashReceived', size: 110, cell: ({ row }) => formatCurrency(row.original.cashReceived) },
    {
      id: 'status', header: 'Close', accessorKey: 'status', size: 100,
      cell: ({ row }) => { const s = FO_STATUS_LABEL[row.original.status] ?? FO_STATUS_LABEL.NONE; return <span className={`badge ${s.badge}`} style={{ fontSize: '0.62rem' }}>{s.label}</span>; },
    },
    {
      id: 'cashDifference', header: 'Difference', accessorKey: 'cashDifference', size: 100,
      cell: ({ row }) => <span style={{ color: row.original.cashDifference ? 'var(--danger, #dc2626)' : undefined }}>{money(row.original.cashDifference)}</span>,
    },
    { id: 'handedOver', header: 'Handed over', accessorKey: 'handedOver', size: 110, cell: ({ row }) => money(row.original.handedOver) },
    { id: 'deposited', header: 'Deposited', accessorKey: 'deposited', size: 100, cell: ({ row }) => money(row.original.deposited) },
    { id: 'retained', header: 'Retained', accessorKey: 'retained', size: 100, cell: ({ row }) => money(row.original.retained) },
    { id: 'closedByName', header: 'Closed by', accessorKey: 'closedByName', size: 120, cell: ({ row }) => row.original.closedByName || '—' },
    { id: 'verifiedByName', header: 'Verified by', accessorKey: 'verifiedByName', size: 120, cell: ({ row }) => row.original.verifiedByName || '—' },
  ], []);

  const receiptColumns = useMemo<SyutGridColumn<FoReceiptRow & { id: string }>[]>(() => [
    { id: 'paymentDate', header: 'Date', accessorKey: 'paymentDate', size: 105, cell: ({ row }) => formatDate(row.original.paymentDate) },
    { id: 'receiptNumber', header: 'Receipt', accessorKey: 'receiptNumber', size: 150, cell: ({ row }) => <span style={{ fontFamily: 'monospace' }}>{row.original.receiptNumber || '—'}</span> },
    {
      id: 'guestName', header: 'Guest / Booking', accessorKey: 'guestName', size: 200,
      cell: ({ row }) => <span>{row.original.guestName || '—'}<div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{row.original.bookingNumber}{row.original.roomNumbers ? ` · ${row.original.roomNumbers}` : ''}</div></span>,
    },
    { id: 'classification', header: 'Kind', accessorKey: 'classification', size: 150, cell: ({ row }) => FO_RECEIPT_KIND_LABEL[row.original.classification] ?? row.original.classification },
    { id: 'paymentMode', header: 'Method', accessorKey: 'paymentMode', size: 110 },
    { id: 'paymentReference', header: 'Reference', accessorKey: 'paymentReference', size: 130, cell: ({ row }) => row.original.paymentReference || '—' },
    { id: 'receivedByName', header: 'By', accessorKey: 'receivedByName', size: 120, cell: ({ row }) => row.original.receivedByName || '—' },
    { id: 'amount', header: 'Amount', accessorKey: 'amount', size: 110, cell: ({ row }) => <b style={{ color: row.original.amount < 0 ? 'var(--danger, #dc2626)' : undefined }}>{formatCurrency(row.original.amount)}</b> },
  ], []);

  const days = useMemo(() => (report?.days ?? []).map((d) => ({ ...d, id: d.businessDate })), [report]);
  const receipts = useMemo(() => (report?.receipts ?? []).map((r) => ({ ...r, id: r.paymentId })), [report]);

  const actionButtons: ActionButton[] = useMemo(() => {
    const b: ActionButton[] = [
      { id: 'view', icon: <IconSystem.list size={16} />, label: view === 'days' ? 'Show receipts' : 'Show days', onClick: () => setView((v) => (v === 'days' ? 'receipts' : 'days')), variant: 'secondary' },
      { id: 'refresh', icon: <IconSystem.refresh size={16} />, label: 'Refresh', onClick: () => { void load(); }, variant: 'icon' },
    ];
    if (allowedActions.includes('export')) {
      b.push({ id: 'export', icon: <IconSystem.download size={16} />, label: exporting ? 'Exporting…' : 'Export', tooltip: 'Export to Excel', onClick: () => { void exportFile('xlsx'); }, variant: 'icon' });
      b.push({ id: 'print', icon: <IconSystem.print size={16} />, label: printing ? 'Preparing…' : 'Print', tooltip: 'Print the report', onClick: () => { void exportFile('pdf'); }, variant: 'icon' });
    }
    return b;
  }, [view, load, allowedActions, exporting, printing, exportFile]);

  const periodLabel = from === to ? formatDate(from) : `${formatDate(from)} – ${formatDate(to)}`;
  const headerItems: ReportHeaderItem[] = [
    { label: 'Property', value: propertyLabel || (propertyId ? '…' : 'Select in filters'), primary: true },
    { label: 'Period', value: periodLabel, primary: true, title: 'Receipt dates; collection point Front Office' },
    ...(report ? [
      { label: 'Received', value: formatCurrency(report.totalReceipts) },
      { label: 'Refunded', value: formatCurrency(report.totalRefunds) },
      { label: 'Net', value: formatCurrency(report.totalReceipts - report.totalRefunds) },
      { label: 'Cash paid out', value: formatCurrency(report.cashPaidOut), title: 'Cash refunds and disbursements paid at the desk' },
    ] : []),
  ];

  const footer = report && report.receipts.length > 0 ? (
    <>
      <b>By method</b>
      {report.modes.map((m) => <span key={m.paymentMode}>{m.paymentMode} <b>{formatCurrency(m.systemAmount)}</b></span>)}
      <b style={{ marginLeft: '0.75rem' }}>By kind</b>
      {Object.entries(report.byClassification).map(([k, v]) => (
        <span key={k}>{FO_RECEIPT_KIND_LABEL[k as keyof typeof FO_RECEIPT_KIND_LABEL] ?? k} <b>{formatCurrency(v ?? 0)}</b></span>
      ))}
    </>
  ) : undefined;

  return (
    <EnterpriseScreenShell
      title="Front Office Collection"
      description="What the Front Office collected and refunded, by method and kind, and each day's close."
      icon={<IconSystem.creditCard size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      filterConfig={{ onClick: () => setFilterOpen((v) => !v), isActive: filterOpen, appliedCount: (from !== monthStartIso() || to !== todayIso()) ? 1 : 0 }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={14} /></button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label={<>Property <span className="text-danger">*</span></>} value={propertyId}
                  onChange={(v) => setPropertyId((v as string) || '')} isAsync loadOptions={loadProperties} defaultOptions cacheOptions={false}
                  isClearable={false} menuPortalTarget={document.body} size="sm" />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From <span className="text-danger">*</span></label>
                <input type="date" className="form-input" value={from} onChange={(e) => setFrom(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To <span className="text-danger">*</span></label>
                <input type="date" className="form-input" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>At most a year.</div>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => { setFrom(monthStartIso()); setTo(todayIso()); }}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader items={headerItems} />
      <ReportBody footer={footer}>
        {!enabled ? (
          <div style={{ color: 'var(--text-muted)', padding: '2rem', textAlign: 'center' }}>Pick a property and a period in the filters.</div>
        ) : error ? (
          <div style={{ color: 'var(--danger, #ef4444)', padding: '2rem', textAlign: 'center' }}>Failed to load — {error}</div>
        ) : view === 'days' ? (
          <SyutGrid data={days} columns={dayColumns} isLoading={loading} emptyMessage="The Front Office took nothing in the period." />
        ) : (
          <SyutGrid data={receipts} columns={receiptColumns} isLoading={loading} emptyMessage="The Front Office took nothing in the period." />
        )}
      </ReportBody>
    </EnterpriseScreenShell>
  );
};

export default FoCollectionReportScreen;
